import { css, cva } from '@styled-system/css';
import { message } from 'antd';
import { Check, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button';

// Carte de plan d'abonnement — style Autocop : card élevée 2xl avec dégradé
// d'accent par tier, badge d'icône en tête, plan recommandé scalé avec glow,
// entrée en cascade (slideInUp + délai par index).

export type PlanAccent = 'neutral' | 'brand' | 'green';

interface PlanFeature {
  label: string;
  value?: string | number;
  isQuota?: boolean;
  icon?: ReactNode;
  /**
   * Icône dédiée de la pastille ronde (30px), prioritaire sur `icon` et sur le check par défaut —
   * ex. `PlatformLogo platform="vestiaire"`. Séparée de `icon` pour ne rien changer au rendu des
   * lignes existantes (produit/vendeur/veilles/image), qui continuent de passer par `icon`.
   */
  tileIcon?: ReactNode;
  /** Plan ne portant pas cette fonctionnalité (ex. Vestiaire sans `vestiaireAccess`) : ligne grisée
   *  et barrée, pastille en croix grisée (remplace systématiquement `icon`/`tileIcon`). */
  excluded?: boolean;
}

interface SubscriptionPlanCardProps {
  planName: string;
  monthlyPrice?: number;
  baseMonthlyPrice?: number;
  freeTrialDays?: number;
  isHighlighted?: boolean;
  subscriptionType: 'subscribe' | 'contact';
  features: PlanFeature[];
  isCurrentPlan?: boolean;
  isDowngrade?: boolean;
  /** Couleur d'accent du tier (badge d'icône + dégradé du haut de card). */
  accent?: PlanAccent;
  /** Icône du plan, affichée dans le badge 52px en tête de card. */
  icon?: ReactNode;
  /** Index pour l'animation d'entrée en cascade. */
  index?: number;
  /** Ligne d'info supplémentaire sous le CTA (ex. quotas de l'essai). */
  trialNote?: string;
  /** Essai en cours : le CTA bascule directement (fin d'essai + débit carte). */
  trialSwitchMode?: boolean;
  /** Plan actuel pendant l'essai (chip « Ton plan actuel (essai) »). */
  isCurrentTrial?: boolean;
  /** Essai en cours, plan porteur : activer le payant tout de suite (fin d'essai + débit). */
  handleActivateNow?: () => void;
  handleSubscribe?: () => Promise<void>;
  handleContact?: () => void;
  handleCancel?: () => void;
}

const card = cva({
  base: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: '2xl',
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: 'border.subtle',
    bg: 'surface.elevated',
    boxShadow: 'sm',
    padding: { base: '7', md: '8' },
    gap: '6',
    transformOrigin: 'center',
    transition: 'transform 220ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 220ms ease, border-color 220ms ease',
    // `backwards` (et non `both`) : sinon le transform final de l'anim écrase le scale au hover.
    animation: 'slideInUp 0.55s cubic-bezier(0.16, 1, 0.3, 1) backwards',
  },
  variants: {
    accent: {
      neutral: {},
      brand: {
        backgroundImage:
          'linear-gradient(180deg, token(colors.brand.primarySoft) 0%, token(colors.surface.elevated) 32%)',
      },
      green: {
        backgroundImage: 'linear-gradient(180deg, token(colors.success.soft) 0%, token(colors.surface.elevated) 32%)',
      },
    },
    popular: {
      true: {
        borderColor: 'border.default',
        // Glow brand pour matcher l'accent du plan recommandé.
        boxShadow:
          '0 24px 56px -26px color-mix(in srgb, token(colors.brand.primary) 55%, transparent), token(shadows.md)',
        transform: { base: 'scale(1)', md: 'scale(1.035)' },
        zIndex: 1,
        _hover: { transform: { base: 'scale(1.03)', md: 'scale(1.075)' }, zIndex: 3 },
      },
      false: {
        _hover: {
          transform: 'scale(1.035)',
          boxShadow: 'lg',
          borderColor: 'border.default',
          zIndex: 2,
        },
      },
    },
  },
});

const popularBadge = css({
  position: 'absolute',
  top: { base: '6', md: '7' },
  right: { base: '6', md: '7' },
  display: 'inline-flex',
  alignItems: 'center',
  paddingX: '3',
  paddingY: '1.5',
  borderRadius: 'full',
  bg: 'brand.primary',
  color: 'white',
  fontSize: 'micro',
  fontWeight: 'bold',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
});

const iconBadge = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '52px',
    height: '52px',
    borderRadius: 'xl',
    flexShrink: 0,
  },
  variants: {
    accent: {
      neutral: { bg: 'surface.sunken', color: 'text.secondary' },
      brand: { bg: 'brand.primarySoft', color: 'brand.primary' },
      green: { bg: 'success.soft', color: 'success.default' },
    },
  },
});

const header = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '3',
});

const planNameStyle = css({
  fontSize: 'h2',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-0.5px',
  textAlign: 'center',
});

// Affiché à la place du bouton quand c'est le plan actuel : clairement inerte.
const currentChip = css({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2',
  width: 'full',
  minHeight: '40px',
  borderRadius: 'lg',
  bg: 'surface.sunken',
  color: 'text.secondary',
  fontSize: 'body',
  fontWeight: 'semibold',
  borderWidth: '1px',
  borderStyle: 'solid',
  borderColor: 'border.subtle',
});

const priceRow = css({
  display: 'flex',
  alignItems: 'baseline',
  gap: '1.5',
  marginTop: '3',
});

const priceMain = css({
  fontSize: 'h1',
  fontWeight: 'bold',
  color: 'text.primary',
  letterSpacing: '-1px',
  lineHeight: '1',
});

const basePriceStyle = css({
  fontSize: 'bodySm',
  color: 'text.tertiary',
  textDecoration: 'line-through',
});

const priceSuffix = css({
  fontSize: 'bodySm',
  fontWeight: 'medium',
  color: 'text.tertiary',
});

const ctaWrap = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '2.5',
});

const helperLine = css({
  fontSize: 'caption',
  color: 'text.tertiary',
  textAlign: 'center',
});

const cancelLink = css({
  alignSelf: 'center',
  fontSize: 'caption',
  color: 'text.tertiary',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  textDecoration: 'underline',
  transition: 'color 120ms ease',
  _hover: { color: 'text.secondary' },
});

const divider = css({
  height: '1px',
  bg: 'border.default',
  marginY: '3',
});

const featuresLabel = css({
  fontSize: 'caption',
  fontWeight: 'semibold',
  color: 'text.tertiary',
  textTransform: 'uppercase',
  letterSpacing: '0.7px',
});

const features = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '5',
  flex: 1,
});

const featureRow = cva({
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '3',
    fontSize: 'body',
    lineHeight: 'body',
    color: 'text.primary',
  },
  variants: {
    excluded: {
      // Mêmes déclarations que `basePriceStyle` (prix barré) — aucune nouvelle classe Panda.
      true: { color: 'text.tertiary' },
      false: {},
    },
  },
});

// Libellé barré d'une ligne exclue — mêmes déclarations que `basePriceStyle`.
const featureLabelExcluded = css({ textDecoration: 'line-through' });

const featIcon = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '30px',
    height: '30px',
    borderRadius: 'lg',
    flexShrink: 0,
  },
  variants: {
    tone: {
      neutral: { bg: 'surface.sunken', color: 'text.secondary' },
      brand: { bg: 'brand.primarySoft', color: 'brand.primary' },
      green: { bg: 'success.soft', color: 'success.default' },
    },
    excluded: {
      // 0.5 : même valeur que les éléments désactivés du design system (`disabled:op_0.5`).
      true: { opacity: 0.5 },
      false: {},
    },
  },
});

const featureValueStyle = css({
  marginLeft: 'auto',
  fontFamily: 'mono',
  fontWeight: 'bold',
  fontSize: 'bodySm',
  color: 'text.primary',
  whiteSpace: 'nowrap',
});

const formatPrice = (value: number, locale: string): string =>
  value.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const SubscriptionPlanCard = ({
  planName,
  monthlyPrice,
  baseMonthlyPrice,
  freeTrialDays = 0,
  isHighlighted = false,
  subscriptionType,
  features: planFeatures,
  isCurrentPlan = false,
  isDowngrade = false,
  accent = 'neutral',
  icon,
  index = 0,
  trialNote,
  trialSwitchMode = false,
  isCurrentTrial = false,
  handleActivateNow,
  handleSubscribe,
  handleContact,
  handleCancel,
}: SubscriptionPlanCardProps) => {
  const { t, i18n } = useTranslation();
  const priceLocale = i18n.language?.startsWith('en') ? 'en-GB' : 'fr-FR';
  const isContact = subscriptionType === 'contact';

  const handleClick = async () => {
    if (isContact) {
      handleContact?.();
      return;
    }
    if (isCurrentPlan || isDowngrade || !handleSubscribe) return;
    try {
      await handleSubscribe();
    } catch (error) {
      console.error('Subscription error:', error);
      // Prix Stripe manquant/invalide, réseau… : sans toast l'échec est invisible
      message.error(t('planCard.paymentError'));
    }
  };

  const buttonLabel = isContact
    ? t('planCard.contactUs')
    : trialSwitchMode
      ? t('planCard.switchNow', { name: planName })
      : freeTrialDays > 0
        ? t('planCard.tryFreeDays', { days: freeTrialDays })
        : t('planCard.choosePlan', { name: planName });

  const helperText = isContact
    ? t('planCard.helperContact')
    : trialSwitchMode
      ? t('planCard.helperTrialSwitch')
      : freeTrialDays > 0
        ? t('planCard.helperTrial', { days: freeTrialDays })
        : t('planCard.helperNoCommitment');

  return (
    <div className={card({ accent, popular: isHighlighted })} style={{ animationDelay: `${index * 90}ms` }}>
      {isHighlighted && !isCurrentPlan && <span className={popularBadge}>{t('planCard.recommended')}</span>}

      <div className={header}>
        {icon && <span className={iconBadge({ accent })}>{icon}</span>}
        <span className={planNameStyle}>{planName}</span>
      </div>

      <div className={priceRow}>
        {monthlyPrice != null ? (
          <>
            {baseMonthlyPrice != null && (
              <span className={basePriceStyle}>{formatPrice(baseMonthlyPrice, priceLocale)}&nbsp;€</span>
            )}
            <span className={priceMain}>{formatPrice(monthlyPrice, priceLocale)}&nbsp;€</span>
            <span className={priceSuffix}>{t('planCard.perMonth')}</span>
          </>
        ) : (
          <span className={priceMain}>{t('planCard.custom')}</span>
        )}
      </div>

      <div className={ctaWrap}>
        {isCurrentPlan || isDowngrade ? (
          <>
            <div className={currentChip}>
              <Check size={15} strokeWidth={2.5} />
              {isCurrentTrial
                ? t('planCard.currentPlanTrial')
                : isDowngrade && !isCurrentPlan
                  ? t('planCard.currentPlanYearly')
                  : t('planCard.currentPlan')}
            </div>
            {isCurrentTrial && handleActivateNow && (
              <>
                <Button variant="primary" size="lg" className={css({ width: 'full' })} onClick={handleActivateNow}>
                  {t('planCard.activateNow', { name: planName })}
                </Button>
                <span className={helperLine}>{t('planCard.activateNowHelper')}</span>
              </>
            )}
            {handleCancel && (
              <button type="button" className={cancelLink} onClick={handleCancel}>
                {t('planCard.cancelSubscription')}
              </button>
            )}
          </>
        ) : (
          <>
            <Button
              variant={isContact ? 'secondary' : isHighlighted ? 'cta' : 'primary'}
              size="lg"
              className={css({ width: 'full' })}
              handleClick={handleClick}
              showLoader
            >
              {buttonLabel}
            </Button>
            <span className={helperLine}>{helperText}</span>
          </>
        )}
        {trialNote && <span className={helperLine}>{trialNote}</span>}
      </div>

      <div className={divider} />

      <span className={featuresLabel}>{t('planCard.included')}</span>
      <ul className={features} aria-label={t('planCard.featuresAria', { name: planName })}>
        {planFeatures.map((feature) => (
          <li
            key={feature.label}
            className={featureRow({ excluded: feature.excluded ?? false })}
            // Ligne exclue : aria-label porte le motif, le barré n'est que visuel.
            aria-label={feature.excluded ? `${feature.label} — ${t('planCard.featureExcludedHint')}` : undefined}
          >
            <span className={featIcon({ tone: accent, excluded: feature.excluded ?? false })}>
              {feature.excluded ? (
                <X size={15} strokeWidth={2} />
              ) : (feature.tileIcon ?? feature.icon ?? <Check size={15} strokeWidth={2} />)}
            </span>
            <span className={feature.excluded ? featureLabelExcluded : undefined}>{feature.label}</span>
            {feature.value != null && <span className={featureValueStyle}>{feature.value}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
};
